import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createContext, useContext, useReducer, useEffect, } from "react";
import { useForm } from "@mantine/form";
const initialState = {
    tokens: [],
    opened: false,
    minimized: false,
    startTransaction: () => null,
    isTransactionRunning: false,
    errors: {},
    finished: false,
    coinConversion: [],
    drawerSize: 0,
};
const reducer = (state, action) => {
    switch (action.type) {
        case "START_TRANSACTION":
            return { ...state, startTransaction: action.payload };
        case "SET_TO_FLOOR_PRICE":
            return {
                ...state,
                tokens: state.tokens.map((token) => {
                    var _a, _b;
                    const price = (_b = (_a = token.token.floor) === null || _a === void 0 ? void 0 : _a.amount) === null || _b === void 0 ? void 0 : _b.native;
                    return { ...token, price: price ? price : token.price };
                }),
            };
        case "SET_TO_TRAIT_PRICE":
            return {
                ...state,
                tokens: state.tokens.map((token) => {
                    var _a;
                    const price = (_a = token.token.topTrait) === null || _a === void 0 ? void 0 : _a.amount.native;
                    return { ...token, price: price ? price : token.price };
                }),
            };
        case "SET_DRAWER_SIZE":
            return { ...state, drawerSize: state.opened ? action.payload : 0 };
        case "SET_COIN_CONVERSION":
            return { ...state, coinConversion: action.payload };
        case "SET_ERROR":
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.payload.chainId]: action.payload.error,
                },
            };
        case "SET_TRANSACTION_RUNNING":
            return { ...state, isTransactionRunning: action.payload };
        case "SET_FINISHED":
            return { ...state, finished: true };
        case "ADD_TOKENS":
            const newTokens = action.payload
                .filter((tokenToAdd) => {
                return !state.tokens.some((token) => token.tokenId === tokenToAdd.tokenId &&
                    token.address.toLowerCase() === tokenToAdd.address.toLowerCase());
            })
                .map((token) => {
                var _a, _b, _c;
                const onchainId = (_b = (_a = token.token) === null || _a === void 0 ? void 0 : _a.collection) === null || _b === void 0 ? void 0 : _b.id;
                const contractRoyaltyBps = (_c = state.tokens.find((token) => { var _a, _b; return ((_b = (_a = token.token) === null || _a === void 0 ? void 0 : _a.collection) === null || _b === void 0 ? void 0 : _b.id) === onchainId; })) === null || _c === void 0 ? void 0 : _c.contractRoyaltyBps;
                return contractRoyaltyBps ? { ...token, contractRoyaltyBps } : token;
            });
            return {
                ...state,
                tokens: [...state.tokens, ...newTokens],
                opened: true,
            };
        case "OPEN_DRAWER":
            return { ...state, opened: true };
        case "MINIMIZE_DRAWER":
            return { ...state, minimized: action.payload };
        case "CLOSE_DRAWER":
            const newState = {
                ...state,
                opened: false,
                errors: {},
                finished: false,
                minimized: false,
                drawerSize: 0,
            };
            if (action.payload) {
                newState.tokens = [];
            }
            return newState;
        case "REMOVE_TOKEN":
            return {
                ...state,
                tokens: state.tokens.filter((token) => token.tokenId !== action.payload.tokenId ||
                    token.address.toLowerCase() !== action.payload.address.toLowerCase()),
            };
        case "UPDATE_TOKEN":
            return {
                ...state,
                tokens: state.tokens.map((token) => token.id === action.payload.id
                    ? { ...token, ...action.payload.token }
                    : token),
            };
        case "UPDATE_ROYALTIES":
            return {
                ...state,
                tokens: state.tokens.map((token) => {
                    var _a, _b;
                    const onchainId = (_b = (_a = token.token) === null || _a === void 0 ? void 0 : _a.collection) === null || _b === void 0 ? void 0 : _b.id;
                    const { royalties, disable } = action.payload;
                    const royalty = royalties.find((royalty) => royalty.onchainId.toLowerCase() === (onchainId === null || onchainId === void 0 ? void 0 : onchainId.toLowerCase()));
                    if (!royalty)
                        return token;
                    const royaltyBps = disable
                        ? 0
                        : !token.contractRoyaltyBps
                            ? royalty.royaltyBps
                            : token.royaltyBps;
                    const contractRoyaltyBps = disable ? undefined : royalty.royaltyBps;
                    return { ...token, contractRoyaltyBps, royaltyBps };
                }),
            };
        default:
            return state;
    }
};
const ListingCartContext = createContext(null);
export const ListingCartProvider = ({ children, }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const form = useForm({
        initialValues: {
            tokens: state.tokens,
        },
        validate: {
            tokens: {
                tokenId: (value) => (!value ? "Token ID is required" : null),
                chainId: (value) => (!value ? "Chain ID is required" : null),
                address: (value) => (!value ? "Address is required" : null),
                quantity: (value) => value <= 0 ? "Quantity must be greater than 0" : null,
                price: (value) => !value || parseFloat(value) <= 0
                    ? "Price must be greater than 0"
                    : null,
                expirationOption: (value) => !(value === null || value === void 0 ? void 0 : value.text) || !(value === null || value === void 0 ? void 0 : value.value)
                    ? "Expiration option is required"
                    : null,
            },
        },
    });
    useEffect(() => {
        form.setFieldValue("tokens", state.tokens);
    }, [state.tokens]);
    return (_jsx(ListingCartContext.Provider, { value: { state, dispatch, form }, children: children }));
};
export const useListingCartContext = () => {
    const context = useContext(ListingCartContext);
    if (!context) {
        throw new Error("useListingCartContext must be used within a ListingCartProvider");
    }
    return context;
};
